import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const TAMANHO_PAGINA_CONTAS = 50;

const montarLabelComCodigo = (codigo, nome) => {
  const codigoTexto = codigo != null ? String(codigo) : '';
  const nomeTexto = nome != null ? String(nome).trim() : '';

  if (codigoTexto && nomeTexto && nomeTexto !== codigoTexto) {
    return `${codigoTexto} - ${nomeTexto}`;
  }

  return codigoTexto || nomeTexto;
};

const obterCodigoConta = conta => (conta ? conta.codigo_conta : null);

const obterNomeConta = conta =>
  conta && conta.apelido_conta ? conta.apelido_conta : '';

const FormParametros = ({
  gruposEmpresa,
  contas,
  moedas,
  codigoGrupoEmpresa,
  codigoConta,
  codigoMoedaForte,
  onGEChange,
  onContaChange,
  onMoedaChange,
  onSimular,
  carregando,
}) => {
  const [buscaConta, setBuscaConta] = useState('');
  const [limiteContas, setLimiteContas] = useState(TAMANHO_PAGINA_CONTAS);

  const opcoesGE = useMemo(
    () =>
      (gruposEmpresa || []).map(ge => {
        const codigo = ge.codigo_grupo_empresa;
        const nome = ge.apelido;
        return {
          value: codigo,
          label: montarLabelComCodigo(codigo, nome),
        };
      }),
    [gruposEmpresa]
  );

  const contasFiltradas = useMemo(() => {
    const termo = buscaConta.trim().toLowerCase();
    const lista = contas || [];

    if (!termo) return lista;

    return lista.filter(conta => {
      const codigoConta = obterCodigoConta(conta);
      const codigo = codigoConta != null ? String(codigoConta) : '';
      const nome = obterNomeConta(conta) || '';
      return `${codigo} ${nome}`.toLowerCase().indexOf(termo) >= 0;
    });
  }, [contas, buscaConta]);

  const opcoesContas = useMemo(
    () =>
      contasFiltradas.slice(0, limiteContas).map(c => ({
        value: obterCodigoConta(c),
        label: montarLabelComCodigo(obterCodigoConta(c), obterNomeConta(c)),
      })),
    [contasFiltradas, limiteContas]
  );

  const opcoesMoedas = useMemo(
    () =>
      (moedas || []).map(m => {
        const codigo = m.codigo_ativo;
        const nome = m.descricao;
        return {
          value: codigo,
          label: montarLabelComCodigo(codigo, nome),
        };
      }),
    [moedas]
  );

  const geValue = opcoesGE.find(o => o.value === codigoGrupoEmpresa) || null;
  const contaSelecionada = (contas || []).find(
    c => String(obterCodigoConta(c)) === String(codigoConta)
  );
  const contaValue = contaSelecionada
    ? {
        value: obterCodigoConta(contaSelecionada),
        label: montarLabelComCodigo(
          obterCodigoConta(contaSelecionada),
          obterNomeConta(contaSelecionada)
        ),
      }
    : null;
  const moedaValue =
    opcoesMoedas.find(o => o.value === codigoMoedaForte) || null;

  useEffect(() => {
    setBuscaConta('');
    setLimiteContas(TAMANHO_PAGINA_CONTAS);
  }, [codigoGrupoEmpresa]);

  const handleBuscaConta = (valor, meta) => {
    if (meta.action !== 'input-change') {
      setBuscaConta('');
      return '';
    }
    setBuscaConta(valor);
    setLimiteContas(TAMANHO_PAGINA_CONTAS);
    return valor;
  };

  const carregarMaisContas = () => {
    setLimiteContas(limite =>
      Math.min(limite + TAMANHO_PAGINA_CONTAS, contasFiltradas.length)
    );
  };

  const handleContaChange = opt => {
    setBuscaConta('');
    onContaChange(opt ? opt.value : null);
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="mb-3">Parâmetros</Card.Title>
        <Row className="align-items-end">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Grupo Empresa *</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={opcoesGE}
                placeholder="Selecione..."
                noOptionsMessage={() => 'Nenhum GE'}
                value={geValue}
                onChange={opt => onGEChange(opt ? opt.value : null)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Conta *</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={opcoesContas}
                placeholder={
                  codigoGrupoEmpresa ? 'Selecione...' : 'Selecione GE primeiro'
                }
                noOptionsMessage={() => 'Nenhuma conta'}
                isDisabled={codigoGrupoEmpresa == null}
                value={contaValue}
                onChange={handleContaChange}
                onInputChange={handleBuscaConta}
                onMenuScrollToBottom={carregarMaisContas}
                filterOption={null}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Moeda Forte *</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={opcoesMoedas}
                placeholder="Selecione..."
                noOptionsMessage={() => 'Nenhuma moeda'}
                value={moedaValue}
                onChange={opt => onMoedaChange(opt ? opt.value : null)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={1}>
            <Form.Group>
              <Form.Label className="small mb-1 d-none d-md-block">
                &nbsp;
              </Form.Label>
              <Button
                variant="primary"
                onClick={onSimular}
                disabled={
                  carregando ||
                  codigoGrupoEmpresa == null ||
                  codigoConta == null ||
                  codigoMoedaForte == null
                }
                className="w-100"
              >
                {carregando ? 'Simulando...' : 'Simular'}
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default FormParametros;
